import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { MessagingConversation } from '../../../api/generated/model/messagingConversation'
import {
  getListMessagingConversationsQueryKey,
  useGetOrCreateMessagingSupportConversation,
  useListMessagingConversations,
} from '../../../api/generated/messaging/messaging'
import { PexIcon } from '../../../components/icons/PexIcon'
import { useT } from '../../../i18n'
import { messagingI18n } from '../../../i18n/messaging'
import { useProblemReportState } from '../../../lib/problem-reporting/host'
import { useMessagingUiStore } from '../../../store/messaging-ui'
import { ConversationList } from './ConversationList'
import { ConversationThread } from './ConversationThread'
import { MessagingShell } from './MessagingShell'
import { MessagingVoiceLeaveGuardContext } from './useMessagingVoiceRecorder'
import { validateConversationProjection } from './entity-safety'
import { useMessagingCapabilities } from './useMessagingCapabilities'
import { useMessagingUnread } from './useMessagingUnread'
import { HelpMenuLauncher } from './HelpMenuLauncher'

type TenantMessagingLauncherProps = {
  surface: 'seller' | 'supply'
}

const CONVERSATION_LIST_PARAMS = { limit: 100 } as const

type VoiceLeaveIntent =
  | { kind: 'close' }
  | { kind: 'back' }
  | { kind: 'switch'; conversationId: string }

export function TenantMessagingLauncher({ surface }: TenantMessagingLauncherProps) {
  const t = useT(messagingI18n)
  const queryClient = useQueryClient()
  const capabilities = useMessagingCapabilities()
  const messagingUnread = useMessagingUnread(surface, capabilities.can_view_inbox)
  const reportHost = useProblemReportState()
  const canOpenReports = Boolean(reportHost.identity)
  const canCreateReport =
    reportHost.phase === 'ready' &&
    Boolean(reportHost.policy?.enabled && reportHost.policy.can_self)
  const [supportError, setSupportError] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [focusComposerId, setFocusComposerId] = useState<string | null>(null)
  const [supportProjection, setSupportProjection] =
    useState<MessagingConversation | null>(null)
  const supportOpeningRef = useRef(false)
  const launcherRef = useRef<HTMLButtonElement>(null)
  const voiceGuardsRef = useRef(
    new Map<string, { discard: () => void; token: symbol }>(),
  )
  const leaveTriggerRef = useRef<HTMLElement | null>(null)
  const focusTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const mountedRef = useRef(true)
  const [dirtyVoiceConversationIds, setDirtyVoiceConversationIds] = useState<
    Set<string>
  >(() => new Set())
  const [pendingVoiceLeaveIntent, setPendingVoiceLeaveIntent] =
    useState<VoiceLeaveIntent | null>(null)
  const open = useMessagingUiStore((state) => state.tenantDrawerOpen)
  const view = useMessagingUiStore((state) => state.tenantDrawerView)
  const activeConversationId = useMessagingUiStore(
    (state) => state.activeConversationId,
  )
  const openDrawer = useMessagingUiStore((state) => state.openTenantDrawer)
  const closeDrawer = useMessagingUiStore((state) => state.closeTenantDrawer)
  const showList = useMessagingUiStore(
    (state) => state.showTenantConversationList,
  )

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (focusTimerRef.current !== undefined) {
        globalThis.clearTimeout(focusTimerRef.current)
      }
    }
  }, [])

  const query = useListMessagingConversations(
    CONVERSATION_LIST_PARAMS,
    {
      query: {
        enabled: capabilities.can_view_inbox,
        retry: false,
        refetchOnWindowFocus: false,
      },
    },
  )
  const supportConversation = useGetOrCreateMessagingSupportConversation()
  const conversations =
    query.data?.status === 200 ? query.data.data.items : []
  const projectionResults = conversations.map((conversation) =>
    validateConversationProjection(conversation, surface),
  )
  const safeConversations = projectionResults.flatMap((result) =>
    result.safe ? [result.value] : [],
  )
  const hasBlockedProjection = projectionResults.some((result) => !result.safe)
  const selected =
    safeConversations.find((item) => item.id === activeConversationId) ??
    (supportProjection?.id === activeConversationId ? supportProjection : null)
  const unreadCount = messagingUnread.unreadMessages
  const shellTitle =
    (view === 'thread' || expanded) && selected
      ? selected.subject || selected.entity_link.label
      : t.launcher.title

  const openSupportConversation = async () => {
    if (supportOpeningRef.current || supportConversation.isPending) return

    supportOpeningRef.current = true
    setSupportError(false)
    try {
      const result = await supportConversation.mutateAsync()
      if (result.status !== 200) throw new Error('Support command failed')

      const projection = validateConversationProjection(result.data, surface)
      if (!projection.safe) throw new Error('Unsafe support projection')

      if (mountedRef.current) {
        setSupportProjection(projection.value)
        setFocusComposerId(projection.value.id)
      }
      openDrawer(projection.value.id)
      void queryClient.invalidateQueries({
        queryKey: getListMessagingConversationsQueryKey(
          CONVERSATION_LIST_PARAMS,
        ),
      })
    } catch {
      if (mountedRef.current) setSupportError(true)
    } finally {
      supportOpeningRef.current = false
    }
  }

  const consumeComposerFocus = useCallback(() => {
    setFocusComposerId(null)
  }, [])

  const openMessenger = async () => {
    if (selected) {
      openDrawer(selected.id)
      return
    }

    const existingSupport = safeConversations.find(
      (conversation) =>
        conversation.topic === 'general' &&
        conversation.entity_link.entity_type === 'organization',
    )
    if (existingSupport) {
      openDrawer(existingSupport.id)
      return
    }

    if (capabilities.can_send) {
      openDrawer()
      await openSupportConversation()
      return
    }

    openDrawer()
  }

  const closeMessengerNow = useCallback(() => {
    setExpanded(false)
    closeDrawer()
    if (focusTimerRef.current !== undefined) {
      globalThis.clearTimeout(focusTimerRef.current)
    }
    focusTimerRef.current = globalThis.setTimeout(
      () => launcherRef.current?.focus(),
      0,
    )
  }, [closeDrawer])

  const runVoiceLeaveIntent = useCallback(
    (intent: VoiceLeaveIntent) => {
      if (intent.kind === 'close') closeMessengerNow()
      else if (intent.kind === 'back') showList()
      else openDrawer(intent.conversationId)
    },
    [closeMessengerNow, openDrawer, showList],
  )

  const requestVoiceLeave = useCallback(
    (intent: VoiceLeaveIntent) => {
      const changesConversation =
        intent.kind !== 'switch' || intent.conversationId !== activeConversationId
      if (
        changesConversation &&
        activeConversationId &&
        dirtyVoiceConversationIds.has(activeConversationId)
      ) {
        leaveTriggerRef.current =
          document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null
        setPendingVoiceLeaveIntent(intent)
        return
      }
      runVoiceLeaveIntent(intent)
    },
    [activeConversationId, dirtyVoiceConversationIds, runVoiceLeaveIntent],
  )

  const voiceLeaveGuard = useMemo(
    () => ({
      register(
        conversationId: string,
        hasUnsavedVoice: boolean,
        discard: () => void,
      ) {
        const token = Symbol(conversationId)
        if (hasUnsavedVoice) {
          voiceGuardsRef.current.set(conversationId, { discard, token })
        } else {
          voiceGuardsRef.current.delete(conversationId)
        }
        setDirtyVoiceConversationIds((current) => {
          if (current.has(conversationId) === hasUnsavedVoice) return current
          const next = new Set(current)
          if (hasUnsavedVoice) next.add(conversationId)
          else next.delete(conversationId)
          return next
        })
        return () => {
          if (voiceGuardsRef.current.get(conversationId)?.token !== token) return
          voiceGuardsRef.current.delete(conversationId)
          setDirtyVoiceConversationIds((current) => {
            if (!current.has(conversationId)) return current
            const next = new Set(current)
            next.delete(conversationId)
            return next
          })
        }
      },
    }),
    [],
  )

  const cancelVoiceLeave = () => {
    setPendingVoiceLeaveIntent(null)
    if (focusTimerRef.current !== undefined) {
      globalThis.clearTimeout(focusTimerRef.current)
    }
    focusTimerRef.current = globalThis.setTimeout(
      () => leaveTriggerRef.current?.focus(),
      0,
    )
  }

  const confirmVoiceLeave = () => {
    if (!pendingVoiceLeaveIntent) return
    const intent = pendingVoiceLeaveIntent
    if (activeConversationId) {
      voiceGuardsRef.current.get(activeConversationId)?.discard()
      voiceGuardsRef.current.delete(activeConversationId)
      setDirtyVoiceConversationIds((current) => {
        const next = new Set(current)
        next.delete(activeConversationId)
        return next
      })
    }
    setPendingVoiceLeaveIntent(null)
    runVoiceLeaveIntent(intent)
  }

  const closeMessenger = () => requestVoiceLeave({ kind: 'close' })
  const backToConversationList = () => requestVoiceLeave({ kind: 'back' })
  const selectConversation = (conversationId: string) =>
    requestVoiceLeave({ kind: 'switch', conversationId })

  const supportAction = capabilities.can_send ? (
    <div className="border-b border-white/10 bg-[#11151c] p-4">
      <p className="mb-3 text-xs leading-5 text-white/55">
        {t.launcher.supportExplanation}
      </p>
      <button
        type="button"
        disabled={supportConversation.isPending}
        onClick={() => void openSupportConversation()}
        className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-white px-4 text-sm font-semibold text-[#12161d] transition hover:bg-white/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 disabled:cursor-wait disabled:opacity-60"
      >
        <PexIcon name="messages" size="xs" decorative />
        {supportConversation.isPending
          ? t.launcher.supportPending
          : t.launcher.messagePexify}
      </button>
      {supportError ? (
        <p
          className="mt-2 text-xs font-medium text-[var(--color-danger)]"
          role="alert"
        >
          {t.launcher.supportError}
        </p>
      ) : null}
    </div>
  ) : null

  const conversationListError = (
    <div className="m-4 rounded-xl border border-rose-400/30 bg-rose-400/10 p-3 text-sm text-rose-100" role="alert">
      <p>{t.states.unavailableDescription}</p>
      <button
        type="button"
        className="mt-2 rounded-lg border border-rose-200/30 px-3 py-1.5 text-xs font-semibold hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
        onClick={() => void query.refetch()}
      >
        {t.conversation.retryList}
      </button>
    </div>
  )

  if (!capabilities.can_view_inbox && !canOpenReports) return null

  return (
    <>
      {!open && <HelpMenuLauncher
        canChat={capabilities.can_view_inbox}
        canOpenReports={canOpenReports}
        canCreateReport={canCreateReport}
        unreadCount={unreadCount}
        onChat={() => { void openMessenger() }}
        launcherRef={launcherRef}
      />}

      {capabilities.can_view_inbox ? (
        <MessagingVoiceLeaveGuardContext.Provider value={voiceLeaveGuard}>
          <MessagingShell
        open={open}
        expanded={expanded}
        title={shellTitle}
        subtitle={t.launcher.teamSupport}
        showBack={!expanded && view === 'thread'}
        onBack={backToConversationList}
        onExpandedChange={setExpanded}
        onClose={closeMessenger}
        backLabel={t.launcher.back}
        expandLabel={t.launcher.expand}
        collapseLabel={t.launcher.collapse}
        closeLabel={t.launcher.close}
      >

          {hasBlockedProjection ? (
            <p
              className="m-3 rounded-[var(--radius-control)] border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
              role="alert"
            >
              {t.states.projectionUnavailable}
            </p>
          ) : null}

          {expanded ? (
            <div className="grid h-full min-h-0 grid-cols-1 sm:grid-cols-[minmax(18rem,21rem)_minmax(0,1fr)]">
              <div className={`min-h-0 overflow-y-auto border-r border-white/10 ${selected ? 'max-sm:hidden' : ''}`}>
                {query.isError ? (
                  conversationListError
                ) : (
                  <ConversationList
                    conversations={safeConversations}
                    selectedId={activeConversationId}
                    onSelect={selectConversation}
                    isLoading={query.isLoading}
                    supportAction={supportAction}
                    variant="expanded"
                  />
                )}
              </div>
              {selected ? (
                <ConversationThread
                  conversation={selected}
                  surface={surface}
                  destination={t.states.pexify}
                  canSend={capabilities.can_send}
                  canUseInternalNotes={false}
                  embedded
                  layout="expanded"
                  focusComposerOnMount={selected.id === focusComposerId}
                  onComposerFocusConsumed={consumeComposerFocus}
                />
              ) : (
                <div className="grid place-items-center p-8 text-sm text-white/55">
                  {t.conversation.noSelection}
                </div>
              )}
            </div>
          ) : view === 'thread' && selected ? (
            <div className="flex h-full min-h-0 flex-col">
              <ConversationThread
                conversation={selected}
                surface={surface}
                destination={t.states.pexify}
                canSend={capabilities.can_send}
                canUseInternalNotes={false}
                embedded
                layout="compact"
                focusComposerOnMount={selected.id === focusComposerId}
                onComposerFocusConsumed={consumeComposerFocus}
              />
            </div>
          ) : (
            <div className="h-full min-h-0 overflow-y-auto">
              {query.isError ? (
                conversationListError
              ) : (
                <ConversationList
                  conversations={safeConversations}
                  selectedId={activeConversationId}
                  onSelect={selectConversation}
                  isLoading={query.isLoading}
                  supportAction={supportAction}
                  variant="compact"
                />
              )}
            </div>
          )}
          {pendingVoiceLeaveIntent ? (
            <div className="fixed inset-0 z-[80] grid place-items-center bg-black/55 p-4">
              <div
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="voice-leave-title"
                aria-describedby="voice-leave-description"
                className="w-full max-w-sm rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface)] p-5 text-[var(--color-text)] shadow-[var(--shadow-float)]"
              >
                <h2 id="voice-leave-title" className="text-base font-semibold">
                  {t.launcher.unsentVoiceTitle}
                </h2>
                <p id="voice-leave-description" className="mt-2 text-sm leading-relaxed text-[var(--color-text-muted)]">
                  {t.launcher.unsentVoiceDescription}
                </p>
                <div className="mt-5 flex justify-end gap-2">
                  <button
                    type="button"
                    autoFocus
                    onClick={cancelVoiceLeave}
                    className="min-h-11 rounded-[var(--radius-control)] border border-[var(--color-border)] bg-[var(--color-surface)] px-4 text-sm font-semibold focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)]"
                  >
                    {t.launcher.keepVoice}
                  </button>
                  <button
                    type="button"
                    onClick={confirmVoiceLeave}
                    className="min-h-11 rounded-[var(--radius-control)] bg-[var(--color-danger)] px-4 text-sm font-semibold text-white focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)]"
                  >
                    {t.launcher.discardVoiceAndContinue}
                  </button>
                </div>
              </div>
            </div>
          ) : null}
          </MessagingShell>
        </MessagingVoiceLeaveGuardContext.Provider>
      ) : null}
    </>
  )
}
